import type { MaybePromise } from "@libs/basic/MaybePromise.js";
import { sleep } from "@libs/basic/sleep.js";
import { diagnostics } from "./diagnostics.js";

export interface RetryResponse {
	retry: boolean;
	after?: number;
}

export class RetryLimitReachedError<TResponse = unknown> extends Error {
	public override readonly name = "RetryLimitReachedError";
	public readonly code = "ERR_RETRY_LIMIT_REACHED";

	constructor(public readonly response: TResponse) {
		super("Retry was requested, but limit has been reached");
	}
}

export async function retry<Output>(
	signal: RequestInit["signal"],
	times: number[],
	callback: () => Promise<Output>,
	shouldRetryOnReject: (error: unknown) => MaybePromise<RetryResponse>,
	shouldRetryOnResolve?: (output: Output) => MaybePromise<RetryResponse>,
): Promise<Output> {
	times = structuredClone(times);
	async function again(error: unknown): Promise<Output> {
		signal?.throwIfAborted();
		const time = times.shift();
		if (time === undefined) {
			throw error;
		}
		diagnostics.debug(`Retrying '${callback.name || callback.toString()}' in ${time}ms`);
		await sleep(time);
		signal?.throwIfAborted();
		return await retry(signal, times, callback, shouldRetryOnReject, shouldRetryOnResolve);
	}

	try {
		const output = await callback();
		const { retry, after } = (await shouldRetryOnResolve?.(output)) ?? { retry: false };
		if (retry) {
			if (after !== undefined && times.length > 0) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				times[0] = Math.max(after, times[0]!);
			}
			return await again(new RetryLimitReachedError(output));
		}
		return output;
	} catch (error) {
		signal?.throwIfAborted();

		if (error instanceof RetryLimitReachedError) {
			throw error;
		}

		const { retry, after } = await shouldRetryOnReject(error);
		if (retry) {
			if (after !== undefined && times.length > 0) {
				times[0] = after;
			}
			diagnostics.warn("Caught error, will be retried", error);
			return await again(error);
		}
		throw error;
	}
}
